/* eslint-disable functional/immutable-data -- Layout arrays are built locally and returned once. */
/**
 * ブロックで文字を組むための 7 段・線幅 2 マスのピクセルフォントと、横に並んだマスを
 * 1×1〜1×4 のブロックへまとめるレイアウト。オープニングの canvas と
 * セクション見出しの SVG（BrickWord）が同じ形を共有する。
 */

const GLYPHS: Record<string, readonly string[]> = {
  A: ["011110", "110011", "110011", "111111", "110011", "110011", "110011"],
  B: ["111110", "110011", "110011", "111110", "110011", "110011", "111110"],
  C: ["011111", "110000", "110000", "110000", "110000", "110000", "011111"],
  D: ["111100", "110110", "110011", "110011", "110011", "110110", "111100"],
  E: ["111111", "110000", "110000", "111110", "110000", "110000", "111111"],
  H: ["110011", "110011", "110011", "111111", "110011", "110011", "110011"],
  I: ["11", "11", "11", "11", "11", "11", "11"],
  L: ["110000", "110000", "110000", "110000", "110000", "110000", "111111"],
  N: ["110011", "111011", "111111", "110111", "110011", "110011", "110011"],
  O: ["011110", "110011", "110011", "110011", "110011", "110011", "011110"],
  P: ["111110", "110011", "110011", "111110", "110000", "110000", "110000"],
  S: ["011111", "110000", "110000", "011110", "000011", "000011", "111110"],
  U: ["110011", "110011", "110011", "110011", "110011", "110011", "011110"],
  Y: ["110011", "110011", "011110", "001100", "001100", "001100", "001100"],
  // 社名の「.」は 2×2 のブロック。オープニングでは最後にはまる 1 ピースになる。
  ".": ["00", "00", "00", "00", "00", "11", "11"],
}

export const GLYPH_ROWS = 7
/** ブロック 1 マスの高さ ÷ 幅（実物の 1×1 ブロックの側面比 9.6mm / 8mm）。 */
export const BRICK_RATIO = 1.2
/** スタッド（上面のポッチ）の高さ ÷ マス幅。 */
export const STUD_RATIO = 0.2

export const BRICK_COLORS = {
  yellow: "#f0d400",
  red: "#f00000",
  blue: "#003cf0",
  ink: "#26242a",
  white: "#fbfaf4",
} as const

export interface LaidBrick {
  col: number
  row: number
  span: number
  color: string
  /** 「.」のブロック。オープニングで最後に落とす。 */
  final: boolean
}

export interface BrickLayout {
  bricks: LaidBrick[]
  cols: number
  rows: number
}

/** 同じ文字列なら毎回同じ色の並びになる、小さな擬似乱数。 */
function seeded(seed: number): () => number {
  let state = seed
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296
    return state / 4294967296
  }
}

/** 横に連続するマス数を 1〜4 のブロックに分ける。段ごとに区切りをずらして実物の積み方に寄せる。 */
function splitRun(length: number, row: number): number[] {
  const spans: number[] = []
  let rest = length
  let first = row % 2 === 0 ? 4 : 3
  while (rest > 0) {
    const span = Math.min(rest, first)
    // 端数 1 を作らないよう、残りが 1 になるときは 1 つ手前で区切る
    const adjusted = rest - span === 1 && span > 2 ? span - 1 : span
    spans.push(adjusted)
    rest -= adjusted
    first = 4
  }
  return spans
}

export function layoutBrickText(
  lines: readonly string[],
  palette: readonly string[],
  seed = 7,
): BrickLayout {
  const random = seeded(seed)
  const lineWidths = lines.map((line) =>
    [...line].reduce(
      (width, char, index) =>
        width + (GLYPHS[char]?.[0]?.length ?? 3) + (index > 0 ? 1 : 0),
      0,
    ),
  )
  const cols = Math.max(...lineWidths)
  const lineGap = 2
  const rows = lines.length * GLYPH_ROWS + (lines.length - 1) * lineGap
  const bricks: LaidBrick[] = []
  // 文字ごとに 1 色。隣の文字と同じ色は避ける
  let previous = ""
  const charColors = lines.map((line) =>
    [...line].map(() => {
      const choices = palette.filter((color) => color !== previous)
      const color =
        choices[Math.floor(random() * choices.length)] ?? BRICK_COLORS.ink
      previous = color
      return color
    }),
  )
  lines.forEach((line, lineIndex) => {
    const top = lineIndex * (GLYPH_ROWS + lineGap)
    // 行ごとに中央へ寄せる
    const indent = Math.floor((cols - (lineWidths[lineIndex] ?? cols)) / 2)
    for (let r = 0; r < GLYPH_ROWS; r += 1) {
      let col = indent
      let run = 0
      let runFinal = false
      let runColor: string = BRICK_COLORS.ink
      const flush = (end: number) => {
        if (run === 0) return
        let start = end - run
        for (const span of splitRun(run, top + r)) {
          bricks.push({
            col: start,
            row: top + r,
            span,
            color: runFinal ? BRICK_COLORS.blue : runColor,
            final: runFinal,
          })
          start += span
        }
        run = 0
      }
      ;[...line].forEach((char, charIndex) => {
        const glyph = GLYPHS[char] ?? ["000"]
        const pattern = glyph[r] ?? ""
        const final = char === "."
        // 文字の切れ目で必ずブロックを区切る
        if (charIndex > 0) {
          flush(col)
          col += 1
        }
        runColor = charColors[lineIndex]?.[charIndex] ?? BRICK_COLORS.ink
        runFinal = final
        for (const cell of pattern) {
          if (cell === "1") {
            run += 1
          } else {
            flush(col)
          }
          col += 1
        }
      })
      flush(col)
    }
  })
  return { bricks, cols, rows }
}

/** #rrggbb を白（amount > 0）か黒（amount < 0）へ寄せる。 */
export function shade(hex: string, amount: number): string {
  const value = Number.parseInt(hex.slice(1), 16)
  const target = amount > 0 ? 255 : 0
  const mix = (channel: number) =>
    Math.round(channel + (target - channel) * Math.abs(amount))
  const r = mix((value >> 16) & 255)
  const g = mix((value >> 8) & 255)
  const b = mix(value & 255)
  return `rgb(${r} ${g} ${b})`
}
